import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EventDateTimePicker } from "@/components/DateTimePicker";

export const PulseEventDialog = ({ open, onOpenChange, eventForm, setEventForm, onSave, onDelete, editingEvent, occupiedTimeSlots }) => {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight">
            {editingEvent ? t("pulsePlanner.editEvent") : t("pulsePlanner.newEvent")}
          </DialogTitle>
          <DialogDescription>{t("pulsePlanner.scheduleEvent")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label>{t("pulsePlanner.titleRequired")}</Label>
            <Input
              value={eventForm.title}
              onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
              placeholder={t("pulsePlanner.eventTitlePlaceholder")}
              data-testid="event-title-input"
              className="rounded-sm"
            />
          </div>

          <EventDateTimePicker
            startValue={eventForm.start_datetime}
            duration={eventForm.duration}
            onStartChange={(value) => setEventForm({ ...eventForm, start_datetime: value })}
            onDurationChange={(value) => setEventForm({ ...eventForm, duration: value })}
            occupiedHours={occupiedTimeSlots}
          />

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("pulsePlanner.repeat")}</Label>
              <Select
                value={eventForm.repeat_rule || "none"}
                onValueChange={(v) => setEventForm({ ...eventForm, repeat_rule: v === "none" ? "" : v })}
              >
                <SelectTrigger data-testid="event-repeat-select" className="rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("pulsePlanner.noRepeat")}</SelectItem>
                  <SelectItem value="daily">{t("pulsePlanner.daily")}</SelectItem>
                  <SelectItem value="weekly">{t("pulsePlanner.weekly")}</SelectItem>
                  <SelectItem value="monthly">{t("pulsePlanner.monthly")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("pulsePlanner.reminder")}</Label>
              <Select
                value={String(eventForm.reminder_minutes || "none")}
                onValueChange={(v) => setEventForm({ ...eventForm, reminder_minutes: v === "none" ? "" : v })}
              >
                <SelectTrigger data-testid="event-reminder-select" className="rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("pulsePlanner.noReminder")}</SelectItem>
                  <SelectItem value="5">{t("pulsePlanner.min5")}</SelectItem>
                  <SelectItem value="15">{t("pulsePlanner.min15")}</SelectItem>
                  <SelectItem value="30">{t("pulsePlanner.min30")}</SelectItem>
                  <SelectItem value="60">{t("pulsePlanner.hour1")}</SelectItem>
                  <SelectItem value="1440">{t("pulsePlanner.day1")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>{t("pulsePlanner.location")}</Label>
            <Input
              value={eventForm.location}
              onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
              placeholder={t("pulsePlanner.locationPlaceholder")}
              className="rounded-sm"
            />
          </div>
          <div>
            <Label>{t("pulsePlanner.notes")}</Label>
            <Textarea
              value={eventForm.notes}
              onChange={(e) => setEventForm({ ...eventForm, notes: e.target.value })}
              placeholder={t("pulsePlanner.notesPlaceholder")}
              rows={2}
              className="rounded-sm"
            />
          </div>
        </div>
        <DialogFooter className="gap-2">
          {editingEvent && (
            <Button variant="destructive" onClick={() => { onDelete(editingEvent.id); onOpenChange(false); }}>
              {t("pulsePlanner.delete")}
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("pulsePlanner.cancel")}</Button>
          <Button onClick={onSave} data-testid="save-event-btn">
            {editingEvent ? t("pulsePlanner.update") : t("pulsePlanner.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
